import TickIcon from '@components/TickIcon'
import TableIconButton from '@components/buttons/TableIconButton'
import type { Company } from '@models/company'
import EditIcon from '@public/icons/edit.svg'
import TrashIcon from '@public/icons/trash.svg'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslation } from 'next-i18next'

interface UseCompanyColumnDefsProps {
  onClickEdit: (companyId: number) => void
  onClickDelete?: (companyId: number) => void
  canDelete?: boolean
}

const useCompanyColumnDefs = ({
  onClickEdit,
  onClickDelete,
  canDelete,
}: UseCompanyColumnDefsProps) => {
  const { t } = useTranslation()

  const columnHelper = createColumnHelper<Company>()

  const columnDefs = [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      cell: info => info.getValue(),
      header: () => t('pages.home.table.name'),
    }),
    columnHelper.accessor(row => row.companyExternalId, {
      id: 'id',
      cell: info => info.getValue(),
      header: () => 'ID',
    }),
    columnHelper.accessor(row => row.countryCode, {
      id: 'countryCode',
      cell: info => info.getValue(),
      header: () => t('pages.home.table.country_code'),
    }),
    columnHelper.accessor(row => row.domen, {
      id: 'domen',
      cell: info => info.getValue(),
      header: () => t('pages.home.table.domen'),
    }),
    columnHelper.accessor(row => row.voipAvailable, {
      id: 'voip',
      cell: info => <TickIcon value={info.getValue()} />,
      header: () => t('pages.home.table.voip'),
    }),
    columnHelper.accessor(row => row.simplifiedOrders, {
      id: 'simplifiedOrders',
      cell: info => <TickIcon value={info.getValue()} />,
      header: () => t('pages.home.table.simplified_orders'),
    }),
    columnHelper.accessor(row => row.proofDelivery.enabled, {
      id: 'proofDelivery',
      cell: info => <TickIcon value={info.getValue()} />,
      header: () => t('pages.home.table.proof_delivery'),
    }),
    columnHelper.accessor(row => row, {
      id: 'editCompany',
      size: 50,
      cell: cell => (
        <div className="flex items-center gap-3">
          <TableIconButton
            onClick={() => onClickEdit(cell.row.original.id)}
            icon={<EditIcon className="h-4 w-4" />}
          />
          {canDelete && onClickDelete && (
            <TableIconButton
              onClick={() => onClickDelete(cell.row.original.id)}
              icon={<TrashIcon className="h-4 w-4" />}
              iconClassName="hover:bg-red-500 hover:text-white text-red-400 bg-danger-100"
            />
          )}
        </div>
      ),
      header: () => <span />,
      enableResizing: false,
      enableSorting: false,
    }),
  ]

  return columnDefs
}

export default useCompanyColumnDefs
